import type {
  AtendimentoDoDia,
  CartaoAgente,
  LinhaFila,
  SetorComAgentes,
  StatusPresenca,
} from '@mymaxx/shared';
import { Aviso, Botao, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellRing,
  ChevronDown,
  ChevronRight,
  Coffee,
  Ear,
  Flame,
  LogOut,
  Power,
  RefreshCw,
  Star,
  ToggleLeft,
  ToggleRight,
  Undo2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfigurarAlertas } from '../alertas/ConfigurarAlertas';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { useSessao } from '../lib/sessao';
import { supervisaoApi } from '../lib/supervisao-api';
import { useSussurros } from '../lib/sussurros';
import { FioDeLeitura } from '../atendimento/Bolha';

/**
 * Monitor: todos os setores empilhados, a equipe em cartões, e as ações onde elas fazem
 * sentido — no cabeçalho do setor quando são do setor, dentro do cartão quando são da
 * pessoa.
 *
 * A ordem dentro do setor vem do servidor e é a ordem do trabalho, não a alfabética: quem
 * está há mais tempo sem receber atendimento aparece primeiro, offline por último. Os
 * tempos chegam em segundos com o instante da apuração, e a tela conta a partir dali — o
 * relógio da máquina de quem olha não entra na conta.
 */
export function MonitorAgentes() {
  const qc = useQueryClient();
  const podeIntervir = useSessao((e) => e.usuario?.permissoes.includes('conversas.intervir'));
  const painel = useQuery({
    queryKey: ['monitor-agentes'],
    queryFn: supervisaoApi.agentes,
    refetchInterval: 15_000,
  });
  const [abertoId, setAbertoId] = useState<string | null>(null);
  const [configurando, setConfigurando] = useState(false);
  const [filaAberta, setFilaAberta] = useState<string | null>(null);
  /** `null` = todos. Quem cuida de dois setores quase sempre quer olhar um de cada vez. */
  const [setorEscolhido, setSetorEscolhido] = useState<string | null>(null);
  const [conversaAberta, setConversaAberta] = useState<{
    id: string;
    protocolo: string;
    cliente: string | null;
  } | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const decorrido = useDecorrido(painel.data?.apuradoEm);

  const recarregar = () => void qc.invalidateQueries({ queryKey: ['monitor-agentes'] });
  const falhou = (e: unknown) =>
    setAviso({
      tipo: 'erro',
      texto: e instanceof ErroApi ? e.mensagem : 'Não consegui fazer isso',
    });
  const feito = (texto: string) => {
    setAviso({ tipo: 'sucesso', texto });
    recarregar();
  };

  const setores = painel.data?.setores ?? [];
  const visiveis = setorEscolhido ? setores.filter((s) => s.id === setorEscolhido) : setores;
  const total = visiveis.reduce((s, st) => s + st.agentes.length, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Monitor</h1>
          <p className="text-sm text-neutral-500">
            {total} {total === 1 ? 'pessoa' : 'pessoas'} em {visiveis.length}{' '}
            {visiveis.length === 1 ? 'setor' : 'setores'}. Quem está há mais tempo sem receber
            aparece primeiro.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* O Monitor é onde se configura o que vai aparecer nas outras telas. */}
          <button
            type="button"
            onClick={() => setConfigurando(true)}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <BellRing size={14} /> Meus alertas
          </button>
          <button
            type="button"
            onClick={() => void painel.refetch()}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <RefreshCw size={14} className={painel.isFetching ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </header>

      {configurando && (
        <ConfigurarAlertas
          setores={setores.map((s) => ({
            id: s.id,
            nome: s.nome,
            naFila: s.naFila,
            emAtendimento: 0,
            online: 0,
          }))}
          aoFechar={() => setConfigurando(false)}
        />
      )}

      {aviso && <Aviso tipo={aviso.tipo === 'erro' ? 'erro' : 'sucesso'}>{aviso.texto}</Aviso>}
      {painel.isError && <Aviso tipo="erro">Não consegui carregar o monitor.</Aviso>}

      {/* Só aparece com mais de um setor: com um só, o filtro seria enfeite. A lista é a
          dos setores que a pessoa acompanha — quem não enxerga um setor não o filtra. */}
      {setores.length > 1 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <Chip
            rotulo="Todos"
            contagem={setores.reduce((s, st) => s + st.naFila, 0)}
            ativo={setorEscolhido === null}
            aoEscolher={() => setSetorEscolhido(null)}
          />
          {setores.map((s) => (
            <Chip
              key={s.id}
              rotulo={s.nome}
              contagem={s.naFila}
              ativo={setorEscolhido === s.id}
              aoEscolher={() => setSetorEscolhido(s.id)}
            />
          ))}
        </div>
      )}

      {visiveis.map((setor) => (
        <Setor
          key={setor.id}
          setor={setor}
          decorrido={decorrido}
          podeIntervir={!!podeIntervir}
          filaAberta={filaAberta === setor.id}
          onAlternarFila={() => setFilaAberta(filaAberta === setor.id ? null : setor.id)}
          onAbrirAgente={setAbertoId}
          onAbrirConversa={setConversaAberta}
          onFeito={feito}
          onErro={falhou}
        />
      ))}

      {conversaAberta && (
        <LeituraDaConversa conversa={conversaAberta} onFechar={() => setConversaAberta(null)} />
      )}

      {abertoId && (
        <Detalhe
          usuarioId={abertoId}
          podeIntervir={!!podeIntervir}
          onFechar={() => setAbertoId(null)}
          onFeito={feito}
          onErro={falhou}
        />
      )}
    </div>
  );
}

function Setor({
  setor,
  decorrido,
  podeIntervir,
  filaAberta,
  onAlternarFila,
  onAbrirAgente,
  onAbrirConversa,
  onFeito,
  onErro,
}: {
  setor: SetorComAgentes;
  decorrido: number;
  podeIntervir: boolean;
  filaAberta: boolean;
  onAlternarFila: () => void;
  onAbrirAgente: (usuarioId: string) => void;
  onAbrirConversa: (c: { id: string; protocolo: string; cliente: string | null }) => void;
  onFeito: (texto: string) => void;
  onErro: (e: unknown) => void;
}) {
  const distribuicao = useMutation({
    mutationFn: (automatica: boolean) => atendimentoApi.definirDistribuicao(setor.id, automatica),
    onSuccess: (r) =>
      onFeito(r.distribuicaoAutomatica ? 'Fila automática ligada.' : 'Fila automática desligada.'),
    onError: onErro,
  });
  const redistribuir = useMutation({
    mutationFn: () => supervisaoApi.redistribuir(setor.id),
    onSuccess: (r) => onFeito(`${r.entregues} atendimento(s) entregue(s).`),
    onError: onErro,
  });

  return (
    <section className="space-y-2">
      {/* O cabeçalho é o comando do setor: abre quem está esperando e carrega o que se faz
          com a fila inteira. */}
      <header className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onAlternarFila}
          className="flex items-center gap-1 text-left"
          aria-expanded={filaAberta}
        >
          {filaAberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-medium">{setor.nome}</span>
          <span
            className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
              setor.naFila > 0
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                : 'text-neutral-500'
            }`}
          >
            {setor.naFila > 0 ? `${setor.naFila} esperando` : 'fila vazia'}
          </span>
        </button>

        {!setor.dentroDoHorario && (
          <span className="text-xs text-neutral-500">fora do horário</span>
        )}

        {podeIntervir && (
          <div className="ml-auto flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              onClick={() => distribuicao.mutate(!setor.distribuicaoAutomatica)}
              title={
                setor.distribuicaoAutomatica
                  ? 'Ligada: a fila cai sozinha em quem está livre'
                  : 'Desligada: a equipe puxa da fila'
              }
              className={`flex items-center gap-1 rounded-full border px-2 py-1 transition ${
                setor.distribuicaoAutomatica
                  ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200'
                  : 'border-neutral-300 text-neutral-500 dark:border-neutral-700'
              }`}
            >
              {setor.distribuicaoAutomatica ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              Fila automática
            </button>
            <button
              type="button"
              disabled={redistribuir.isPending || setor.naFila === 0}
              onClick={() => redistribuir.mutate()}
              className="flex items-center gap-1 rounded-full border border-neutral-300 px-2 py-1 text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-40 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <RefreshCw size={13} /> Redistribuir
            </button>
          </div>
        )}
      </header>

      {filaAberta && (
        <FilaDoSetor
          setorId={setor.id}
          podeIntervir={podeIntervir}
          onAbrirConversa={onAbrirConversa}
          onFeito={onFeito}
          onErro={onErro}
        />
      )}

      {/* "Ninguém de plantão", e não "ninguém vinculado": quem administra os setores fica
          de fora destes cartões mesmo estando vinculado, e a frase antiga mentiria. */}
      {setor.agentes.length === 0 ? (
        <p className="text-sm text-neutral-500">Ninguém de plantão neste setor.</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-2">
          {setor.agentes.map((a) => (
            <Cartao
              key={`${setor.id}-${a.usuarioId}`}
              agente={a}
              decorrido={decorrido}
              onAbrir={() => onAbrirAgente(a.usuarioId)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * O que está acontecendo no setor agora: quem espera na fila e quem já está sendo
 * atendido, com quem.
 *
 * A parte de baixo nasceu de uma queixa direta (29/09): quem cuida de dois setores abria
 * o Monitor e "não via nada da Liberação", porque a fila estava vazia — o atendimento
 * estava em curso, e atendimento em curso não aparecia em lugar nenhum desta tela. O
 * servidor já mandava a lista; faltava mostrá-la.
 */
function FilaDoSetor({
  setorId,
  podeIntervir,
  onAbrirConversa,
  onFeito,
  onErro,
}: {
  setorId: string;
  podeIntervir: boolean;
  onAbrirConversa: (c: { id: string; protocolo: string; cliente: string | null }) => void;
  onFeito: (texto: string) => void;
  onErro: (e: unknown) => void;
}) {
  const qc = useQueryClient();
  const painel = useQuery({
    queryKey: ['monitor-fila', setorId],
    queryFn: () => supervisaoApi.painel(setorId),
    refetchInterval: 15_000,
  });
  const prioridade = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: boolean }) =>
      supervisaoApi.prioridade(id, valor),
    onSuccess: (_, { valor }) => {
      void qc.invalidateQueries({ queryKey: ['monitor-fila', setorId] });
      onFeito(valor ? 'Marcado como prioritário.' : 'Prioridade retirada.');
    },
    onError: onErro,
  });

  const fila = painel.data?.fila ?? [];
  const emAtendimento = painel.data?.emAtendimento ?? [];

  return (
    <div className="rounded-card space-y-3 border border-neutral-200 p-3 dark:border-neutral-800">
      {painel.isPending && <p className="text-sm text-neutral-500">Carregando o setor…</p>}

      <p className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
        Na fila {fila.length > 0 && `(${fila.length})`}
      </p>
      {painel.isSuccess && fila.length === 0 && (
        <p className="text-sm text-neutral-500">Ninguém esperando.</p>
      )}
      <ul className="space-y-1">
        {fila.map((c: LinhaFila) => (
          <li
            key={c.conversaId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm dark:border-neutral-700"
          >
            <div className="min-w-0">
              <p className="truncate">{c.cliente ?? 'Sem identificação'}</p>
              <p className="truncate font-mono text-[11px] text-neutral-500">{c.protocolo}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-[11px] text-neutral-500">
                esperando há {duracao(c.esperandoSeg)}
              </span>
              {podeIntervir && (
                <button
                  type="button"
                  onClick={() => prioridade.mutate({ id: c.conversaId, valor: !c.prioritaria })}
                  title={c.prioritaria ? 'Tirar a prioridade' : 'Passar na frente da fila'}
                  className={c.prioritaria ? 'text-amber-500' : 'text-neutral-400'}
                >
                  <Flame size={15} className={c.prioritaria ? 'fill-amber-500' : ''} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <p className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
        Em atendimento {emAtendimento.length > 0 && `(${emAtendimento.length})`}
      </p>
      {painel.isSuccess && emAtendimento.length === 0 && (
        <p className="text-sm text-neutral-500">Nada em curso neste setor.</p>
      )}
      <ul className="space-y-1">
        {emAtendimento.map((c) => (
          <li key={c.conversaId}>
            <button
              type="button"
              onClick={() =>
                onAbrirConversa({ id: c.conversaId, protocolo: c.protocolo, cliente: c.cliente })
              }
              className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-left text-sm transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              <div className="min-w-0">
                <p className="truncate">{c.cliente ?? 'Sem identificação'}</p>
                <p className="truncate font-mono text-[11px] text-neutral-500">{c.protocolo}</p>
              </div>
              <div className="shrink-0 text-right text-[11px] text-neutral-500">
                <p>{c.atendente ? `com ${c.atendente.nome}` : 'sem atendente'}</p>
                <p className={c.aguardandoResposta ? 'text-amber-600 dark:text-amber-400' : ''}>
                  {c.aguardandoResposta
                    ? `cliente espera há ${duracao(c.semMexerSeg)}`
                    : `há ${duracao(c.desdeSeg)} em atendimento`}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Um dos setores no filtro do alto, com quantos esperam nele. */
function Chip({
  rotulo,
  contagem,
  ativo,
  aoEscolher,
}: {
  rotulo: string;
  contagem: number;
  ativo: boolean;
  aoEscolher: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoEscolher}
      aria-pressed={ativo}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
        ativo
          ? 'border-marca-500 bg-marca-50 font-medium text-marca-800 dark:bg-marca-950 dark:text-marca-100'
          : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
      }`}
    >
      {rotulo}
      {contagem > 0 && (
        <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-medium text-white">
          {contagem}
        </span>
      )}
    </button>
  );
}

/** A conversa de outra pessoa, aberta do Monitor só para ler. */
function LeituraDaConversa({
  conversa,
  onFechar,
}: {
  conversa: { id: string; protocolo: string; cliente: string | null };
  onFechar: () => void;
}) {
  const mensagens = useQuery({
    queryKey: ['monitor-conversa', conversa.id],
    queryFn: () => supervisaoApi.conversa(conversa.id),
    refetchInterval: 10_000,
  });

  return (
    <Modal
      aberto
      titulo={conversa.cliente ?? 'Atendimento'}
      descricao={conversa.protocolo}
      onFechar={onFechar}
      largura="larga"
      alturaCheia
    >
      <FioDeLeitura
        mensagens={mensagens.data ?? []}
        carregando={mensagens.isPending}
        vazio="Esta conversa ainda não tem mensagens."
      />
    </Modal>
  );
}

/** Segundos decorridos desde a apuração do servidor, contados de segundo em segundo. */
function useDecorrido(apuradoEm?: string): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!apuradoEm) return 0;
  return Math.max(0, Math.round((agora - new Date(apuradoEm).getTime()) / 1000));
}

const CORES: Record<StatusPresenca, string> = {
  online: 'border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950/40',
  pausa: 'border-blue-300 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40',
  offline: 'border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900',
};

const ROTULO: Record<StatusPresenca, string> = {
  online: 'Disponível',
  pausa: 'Em pausa',
  // 'Desconectado' e não 'Ausente': desde 28/09 ninguém fica ausente por acaso — entrar
  // já deixa disponível e recarregar não muda nada. Quem aparece assim saiu do sistema,
  // nunca entrou, ou foi desconectado pela supervisão.
  offline: 'Desconectado',
};

function Cartao({
  agente,
  decorrido,
  onAbrir,
}: {
  agente: CartaoAgente;
  decorrido: number;
  onAbrir: () => void;
}) {
  const emAtendimento = agente.ativas > 0;
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={`rounded-card border p-3 text-left transition hover:brightness-95 ${CORES[agente.status]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate font-medium">{agente.nome}</p>
        <span className="shrink-0 text-[11px] text-neutral-500">
          {emAtendimento ? `${agente.ativas}/${agente.limite}` : ROTULO[agente.status]}
        </span>
      </div>

      <p className="mt-1 truncate text-xs text-neutral-500">
        {agente.status === 'pausa' && (agente.motivoPausa ?? 'Pausa')}
        {agente.status === 'online' &&
          (emAtendimento ? `${agente.ativas} em atendimento` : 'Livre')}
        {agente.status === 'offline' && 'Fora do sistema'}
      </p>

      <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-500">
        <Relogio agente={agente} decorrido={decorrido} />
        <span className="flex items-center gap-2">
          {agente.encerradosHoje > 0 && <span>{agente.encerradosHoje} hoje</span>}
          {agente.notaMedia !== null && (
            <span className="flex items-center gap-0.5">
              <Star size={10} className="fill-amber-400 text-amber-400" />
              {agente.notaMedia.toFixed(1)}
            </span>
          )}
        </span>
      </div>
    </button>
  );
}

/**
 * O relógio do cartão: cada situação tem um tempo que importa, e só um.
 *
 * Mostrar sempre "sem receber há" dizia pouco — para quem está em pausa o número só cresce,
 * para quem está fora do sistema ele mede a coisa errada, e para quem está atendendo o que
 * interessa é o cliente do outro lado (pedido de 03/10).
 *
 * Em atendimento, a espera do cliente ganha da duração: é sobre ela que a supervisão age, e
 * por isso ela vem em âmbar. Sem espera, a duração do caso mais antigo.
 */
function Relogio({ agente, decorrido }: { agente: CartaoAgente; decorrido: number }) {
  if (agente.status === 'pausa') {
    return (
      <span title="Tempo nesta pausa">
        em pausa há {duracao((agente.pausaSeg ?? 0) + decorrido)}
      </span>
    );
  }
  if (agente.status === 'offline') {
    return agente.offlineSeg === null ? (
      <span title="Nunca entrou no sistema">nunca entrou</span>
    ) : (
      <span title="Tempo fora do sistema">fora há {duracao(agente.offlineSeg + decorrido)}</span>
    );
  }
  if (agente.aguardandoSeg !== null) {
    return (
      <span className="text-amber-600 dark:text-amber-400" title="O cliente espera resposta">
        cliente espera há {duracao(agente.aguardandoSeg + decorrido)}
      </span>
    );
  }
  if (agente.atendendoSeg !== null) {
    return (
      <span title="Tempo no atendimento mais antigo que ele tem aberto">
        atendendo há {duracao(agente.atendendoSeg + decorrido)}
      </span>
    );
  }
  return (
    <span title="Tempo desde o último atendimento recebido">
      {agente.ociosoSeg === null
        ? 'nunca recebeu'
        : `livre há ${duracao(agente.ociosoSeg + decorrido)}`}
    </span>
  );
}

function Detalhe({
  usuarioId,
  podeIntervir,
  onFechar,
  onFeito,
  onErro,
}: {
  usuarioId: string;
  podeIntervir: boolean;
  onFechar: () => void;
  onFeito: (texto: string) => void;
  onErro: (e: unknown) => void;
}) {
  const qc = useQueryClient();
  const detalhe = useQuery({
    queryKey: ['monitor-agente', usuarioId],
    queryFn: () => supervisaoApi.detalheAgente(usuarioId),
    refetchInterval: 20_000,
  });
  const decorrido = useDecorrido(new Date().toISOString());
  // O atendimento aberto à direita. Guarda protocolo e cliente junto porque a coluna da
  // direita precisa se apresentar sem depender de achar a linha de novo na lista.
  const [aberta, setAberta] = useState<{
    id: string;
    protocolo: string;
    cliente: string | null;
  } | null>(null);
  const abrirSussurro = useSussurros((e) => e.abrir);
  const d = detalhe.data;

  const recarregar = () => void qc.invalidateQueries({ queryKey: ['monitor-agente', usuarioId] });

  // Quem mais atende os mesmos setores — o destino possível de uma transferência.
  const painel = useQuery({ queryKey: ['monitor-agentes'], queryFn: supervisaoApi.agentes });
  const colegas = (painel.data?.setores ?? [])
    .flatMap((st) => st.agentes)
    .filter((a, i, todos) => todos.findIndex((o) => o.usuarioId === a.usuarioId) === i);

  const presenca = useMutation({
    mutationFn: (status: 'online' | 'pausa') => supervisaoApi.presenca(usuarioId, status),
    onSuccess: (_, status) => {
      recarregar();
      onFeito(status === 'pausa' ? 'Pessoa colocada em pausa.' : 'Pessoa liberada da pausa.');
    },
    onError: onErro,
  });
  const limite = useMutation({
    mutationFn: (valor: number) => supervisaoApi.limite(usuarioId, valor),
    onSuccess: () => {
      recarregar();
      onFeito('Limite alterado.');
    },
    onError: onErro,
  });
  const desconectar = useMutation({
    mutationFn: () => supervisaoApi.desconectar(usuarioId),
    onSuccess: () => {
      recarregar();
      onFeito('Sessão encerrada; a pessoa saiu do sistema.');
    },
    onError: onErro,
  });
  const transferir = useMutation({
    mutationFn: ({ conversaId, para }: { conversaId: string; para: string }) =>
      supervisaoApi.transferirPara(conversaId, para),
    onSuccess: () => {
      recarregar();
      onFeito('Atendimento entregue a outra pessoa.');
    },
    onError: onErro,
  });
  const devolver = useMutation({
    mutationFn: (conversaId: string) => supervisaoApi.devolver(conversaId),
    onSuccess: () => {
      recarregar();
      onFeito('Atendimento devolvido para a fila.');
    },
    onError: onErro,
  });

  return (
    <Modal aberto titulo={d?.nome ?? 'Agente'} onFechar={onFechar} largura="cheia" alturaCheia>
      {detalhe.isPending && <p className="text-sm text-neutral-500">Carregando…</p>}
      {d && (
        // Duas colunas: à esquerda quem é a pessoa e o que ela está fazendo; à direita a
        // conversa escolhida, em formato de chat. Antes o fio abria por baixo da linha,
        // numa tira de poucos centímetros e sem mídia — havia espaço de sobra ao lado.
        // Em tela estreita as duas viram uma coluna só, e o chat recebe uma altura mínima:
        // sem ela a lista de cima empurrava a conversa para uma faixa de dois dedos.
        <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(16rem,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:grid-rows-1">
          <div className="min-h-0 space-y-5 overflow-y-auto pr-1">
            <div className="flex flex-wrap gap-6 text-sm">
              <Numero rotulo="Situação" valor={d.motivoPausa ?? ROTULO[d.status]} />
              <Numero rotulo="Em atendimento" valor={`${d.ativas}/${d.limite}`} />
              {/* O mesmo critério do cartão: cada situação tem um relógio, e só um. */}
              {d.status === 'pausa' ? (
                <Numero rotulo="Em pausa há" valor={duracao((d.pausaSeg ?? 0) + decorrido)} />
              ) : d.status === 'offline' ? (
                <Numero
                  rotulo="Fora do sistema há"
                  valor={d.offlineSeg === null ? 'nunca entrou' : duracao(d.offlineSeg + decorrido)}
                />
              ) : (
                <Numero
                  rotulo="Sem receber há"
                  valor={d.ociosoSeg === null ? '—' : duracao(d.ociosoSeg + decorrido)}
                />
              )}
              {d.aguardandoSeg !== null && (
                <Numero rotulo="Cliente espera há" valor={duracao(d.aguardandoSeg + decorrido)} />
              )}
              <Numero rotulo="Encerrados hoje" valor={String(d.encerradosHoje)} />
              <Numero rotulo="TMA de hoje" valor={d.tmaSeg === null ? '—' : duracao(d.tmaSeg)} />
              <Numero
                rotulo="Nota de hoje"
                valor={d.notaMedia === null ? '—' : d.notaMedia.toFixed(1)}
              />
            </div>

            <p className="text-xs text-neutral-500">Setores: {d.setores.join(', ') || '—'}</p>

            {podeIntervir && (
              <div className="flex flex-wrap items-center gap-2">
                {/* Pausa só tem sentido para quem está conectado: colocar em pausa alguém
                  ausente só troca um rótulo por outro, e tirar da pausa o devolveria para
                  "disponível" sem que ele esteja em lugar nenhum. */}
                {d.status === 'pausa' && (
                  <Botao variante="secundario" onClick={() => presenca.mutate('online')}>
                    <Power size={15} /> Tirar da pausa
                  </Botao>
                )}
                {d.status === 'online' && (
                  <>
                    <Botao variante="secundario" onClick={() => presenca.mutate('pausa')}>
                      <Coffee size={15} /> Colocar em pausa
                    </Botao>
                    <Botao variante="secundario" onClick={() => desconectar.mutate()}>
                      <LogOut size={15} /> Desconectar
                    </Botao>
                  </>
                )}
                <label className="flex items-center gap-2 text-sm text-neutral-500">
                  Limite
                  <input
                    type="number"
                    min={1}
                    max={50}
                    defaultValue={d.limite}
                    onBlur={(e) => {
                      const valor = Number(e.target.value);
                      if (valor && valor !== d.limite) limite.mutate(valor);
                    }}
                    className="w-16 rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                  />
                </label>
              </div>
            )}

            <section className="space-y-2">
              <h3 className="text-sm font-medium">Em atendimento agora</h3>
              {d.abertos.length === 0 ? (
                <p className="text-sm text-neutral-500">Nada em curso.</p>
              ) : (
                <ul className="space-y-1">
                  {d.abertos.map((a) => (
                    // Duas linhas, e não uma: abrir a conversa é a ação principal e ocupa a
                    // largura inteira. Enfileirado com os tempos, o sussurro, o devolver e o
                    // seletor de transferência, o clique que abre o atendimento sobrava
                    // espremido em poucos pixels — e era justamente o que o supervisor
                    // tentava acertar (relato de 30/09).
                    <li
                      key={a.conversaId}
                      className={`rounded-lg border text-sm ${
                        aberta?.id === a.conversaId
                          ? 'border-marca-400 bg-marca-50 dark:border-marca-700 dark:bg-marca-950/40'
                          : 'border-neutral-200 dark:border-neutral-700'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setAberta({
                            id: a.conversaId,
                            protocolo: a.protocolo,
                            cliente: a.cliente,
                          })
                        }
                        className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left"
                      >
                        <span className="min-w-0">
                          <span className="block truncate">{a.cliente ?? 'Sem identificação'}</span>
                          <span className="block truncate font-mono text-[11px] text-neutral-500">
                            {a.protocolo}
                          </span>
                        </span>
                        <span className="shrink-0 text-right text-[11px] text-neutral-500">
                          <span className="block">
                            há {duracao(a.comEleSeg + decorrido)} com ele
                          </span>
                          {a.aguardandoSeg !== null && (
                            <span className="block text-amber-600">
                              cliente espera há {duracao(a.aguardandoSeg + decorrido)}
                            </span>
                          )}
                        </span>
                      </button>
                      {podeIntervir && (
                        <div className="flex flex-wrap items-center justify-end gap-3 px-3 pb-2">
                          <button
                            type="button"
                            title="Falar no ouvido de quem atende"
                            onClick={() => abrirSussurro(a.conversaId, a.protocolo)}
                            className="text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
                          >
                            <Ear size={16} />
                          </button>
                          <button
                            type="button"
                            title="Devolver para a fila"
                            onClick={() => devolver.mutate(a.conversaId)}
                            className="text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
                          >
                            <Undo2 size={16} />
                          </button>
                          {/* Ou entregar na mão de alguém: o supervisor que sabe quem tem
                              contexto não precisa torcer para a fila acertar. */}
                          <select
                            value=""
                            aria-label="Transferir para"
                            onChange={(e) => {
                              if (e.target.value)
                                transferir.mutate({
                                  conversaId: a.conversaId,
                                  para: e.target.value,
                                });
                            }}
                            className="rounded border border-neutral-300 px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                          >
                            <option value="">Passar para…</option>
                            {colegas
                              .filter((c) => c.usuarioId !== usuarioId)
                              .map((c) => (
                                <option key={c.usuarioId} value={c.usuarioId}>
                                  {c.nome} — {ROTULO[c.status]}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium">Atendimentos de hoje</h3>
              {d.doDia.length === 0 ? (
                <p className="text-sm text-neutral-500">Nenhum hoje.</p>
              ) : (
                <ul className="space-y-1">
                  {d.doDia.map((a) => (
                    <LinhaDoDia
                      key={a.id}
                      atendimento={a}
                      selecionada={aberta?.id === a.id}
                      onAbrir={() =>
                        setAberta({ id: a.id, protocolo: a.protocolo, cliente: a.cliente })
                      }
                    />
                  ))}
                </ul>
              )}
            </section>
          </div>

          <ConversaAoLado conversa={aberta} />
        </div>
      )}
    </Modal>
  );
}

/** A coluna da direita: o atendimento escolhido, lido como chat. */
function ConversaAoLado({
  conversa,
}: {
  conversa: { id: string; protocolo: string; cliente: string | null } | null;
}) {
  const mensagens = useQuery({
    queryKey: ['monitor-conversa', conversa?.id],
    queryFn: () => supervisaoApi.conversa(conversa!.id),
    enabled: !!conversa,
  });

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
      {!conversa ? (
        <p className="m-auto max-w-56 text-center text-sm text-neutral-500">
          Escolha um atendimento à esquerda para ler a conversa aqui.
        </p>
      ) : (
        <>
          <header className="shrink-0 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700">
            <p className="truncate text-sm font-medium">
              {conversa.cliente ?? 'Sem identificação'}
            </p>
            <p className="truncate font-mono text-[11px] text-neutral-500">{conversa.protocolo}</p>
          </header>
          <FioDeLeitura
            mensagens={mensagens.data ?? []}
            carregando={mensagens.isPending}
            vazio="Esta conversa não tem mensagens."
          />
        </>
      )}
    </aside>
  );
}

function LinhaDoDia({
  atendimento,
  selecionada,
  onAbrir,
}: {
  atendimento: AtendimentoDoDia;
  selecionada: boolean;
  onAbrir: () => void;
}) {
  return (
    <li
      className={`rounded-lg border ${
        selecionada
          ? 'border-marca-400 bg-marca-50 dark:border-marca-700 dark:bg-marca-950/40'
          : 'border-neutral-200 dark:border-neutral-700'
      }`}
    >
      <button
        type="button"
        onClick={onAbrir}
        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left text-sm"
      >
        <div className="min-w-0">
          <p className="truncate">{atendimento.cliente ?? 'Sem identificação'}</p>
          <p className="truncate font-mono text-[11px] text-neutral-500">{atendimento.protocolo}</p>
        </div>
        <div className="shrink-0 text-right text-[11px] text-neutral-500">
          <p>{atendimento.duracaoSeg === null ? 'em aberto' : duracao(atendimento.duracaoSeg)}</p>
          {atendimento.avaliacao !== null && (
            <p className="flex items-center justify-end gap-0.5">
              <Star size={10} className="fill-amber-400 text-amber-400" />
              {atendimento.avaliacao}
            </p>
          )}
        </div>
      </button>
    </li>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <p className="font-semibold">{valor}</p>
      <p className="text-xs text-neutral-500">{rotulo}</p>
    </div>
  );
}

/** Duração em linguagem de gente: "38s", "12min", "2h 05min". */
function duracao(segundos: number): string {
  if (segundos < 60) return `${segundos}s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos}min`;
  return `${Math.floor(minutos / 60)}h ${String(minutos % 60).padStart(2, '0')}min`;
}
